import { graphlib } from "@dagrejs/dagre";
import { filter, find, forEach, groupBy } from "lodash";
import ordinal from "ordinal";
import { type ReleaseDocuments, findReleaseDocument, isDocumentId } from "../DocumentUtils";
import { processText } from "../TemplateUtils";
import { COMPILER_ERROR_TYPES } from "../CompilerErrorType";
import type { CompilerError } from "../MiscUtils";
import type { GraphNode, NodeKey } from "./GraphTypes";

export const getNodeResult = (node: GraphNode) => {
  if (!node) return;
  return typeof node.input === "undefined" ? node.derived : node.input;
};

export const getNamespacedDescription = (node: GraphNode, namespace?: string): string | undefined => {
  if (!node) return;
  return node.description ? `${namespace}/${normalizeText(node.description)}` : undefined;
};

export const getOrdinal = (node: any, pass: any, returnIndex?: any) => {
  let desc: string | number;
  // This doesn't work well with sub-entities (the index doesn't get reset)
  const indexes = groupBy(filter(pass.graph._nodes, { entity: node.entity, identifier: true, hidden: false }), "index");
  const myIndex = Object.keys(indexes).indexOf(node.index);

  if (typeof myIndex === "undefined" || myIndex === -1) {
    //entities[node.entity]
    desc = returnIndex ? node.index : `#${node.index}`;
  } else {
    desc = returnIndex ? myIndex + 1 : ordinal(myIndex + 1);
  }
  return desc;
};

const isNormalizableWhitespace = (char: string) => {
  const code = char.charCodeAt(0);
  return (
    code <= 0x20 ||
    code === 0x00a0 ||
    code === 0x1680 ||
    (code >= 0x2000 && code <= 0x200a) ||
    code === 0x2028 ||
    code === 0x2029 ||
    code === 0x202f ||
    code === 0x205f ||
    code === 0x3000 ||
    code === 0xfeff
  );
};

export const normalizeText = (text: string) => {
  if (!text) {
    return text;
  }

  let result = "";
  let pendingWhitespace = false;

  for (const char of text.toString().normalize("NFD")) {
    if (isNormalizableWhitespace(char)) {
      pendingWhitespace = result.length > 0;
      continue;
    }

    if (pendingWhitespace) {
      result += " ";
      pendingWhitespace = false;
    }

    switch (char) {
      case "\u2018":
      case "\u2019":
        result += "'";
        break;
      case "\u201c":
      case "\u201d":
        result += '"';
        break;
      case "\u2013":
      case "\u2014":
        result += "-";
        break;
      default:
        result += char;
    }
  }

  return result;
};

/**
 * Validity rules for an attribute description parsed as a standalone NL attribute
 * reference (see Parser.ts's createAttributeExpression). Not a universal rule for
 * every description in the graph — decision table headers/conclusions and rule
 * sheet cell text legitimately allow `/` (e.g. "personal/carer's leave").
 */
export const getInvalidAttributeDescriptionErrors = (description: string): CompilerError[] => {
  const errors: CompilerError[] = [];
  if (description.includes("/")) {
    errors.push({
      type: COMPILER_ERROR_TYPES.INVALID_ATTRIBUTE_DESCRIPTION.id,
      message: "Invalid attribute, contains '/'",
    });
  }
  if (description.endsWith("=")) {
    errors.push({
      type: COMPILER_ERROR_TYPES.INVALID_ATTRIBUTE_DESCRIPTION.id,
      message: "Invalid attribute, cannot end with '='",
    });
  }
  if (description.endsWith(",")) {
    errors.push({
      type: COMPILER_ERROR_TYPES.INVALID_ATTRIBUTE_DESCRIPTION.id,
      message: "Invalid attribute, cannot end with ','",
    });
  }
  return errors;
};

export const hasInvalidAttributeDescription = (description: string): boolean =>
  getInvalidAttributeDescriptionErrors(description).length > 0;

export type SlashHandlingMode = "reject" | "encode" | "passthrough";

/**
 * Decision table conclusions/headers, identifier merge, and rule sheet cell text
 * (unlike the standalone NL attribute grammar above) have real production usage of
 * `/` as ordinary text (e.g. "personal/carer's leave"), so they can't share the
 * strict reject-on-`/` behavior by default. Driven by the release's `disableGuids`
 * opt-in (the project-level `Model.disableGuids` switch): once a release has opted
 * in — signalling its node
 * ids are moving off random GUIDs onto normalized descriptions — `/` must never
 * reach an id, so it's rejected outright rather than silently encoded, since a
 * release opting in is expected to have already cleaned up its content. Everyone
 * else defaults to "passthrough" — node ids stay GUIDs regardless of `/` in the
 * description, so there's nothing to protect and no reason to alter content that
 * already exists in production documents. "encode" is not currently reachable via
 * this helper; it's kept for a future opt-in path (e.g. once ids are actually
 * minted from descriptions) that wants `/` preserved-but-safe rather than banned.
 */
export const getAttributeDescriptionSlashMode = (disableGuids?: boolean): SlashHandlingMode =>
  disableGuids ? "reject" : "passthrough";

/**
 * Handles a description that may contain `/` per the given mode: "passthrough"
 * leaves it untouched (safe as long as nothing downstream mints an id from the
 * description); "encode" replaces it with the URL-safe escape (`%2F`) so it can
 * never be mistaken for a path separator by downstream `/`-splitting code
 * (GraphExtractUtil, GraphFind, GraphTraversal, and their Rust mirrors); "reject"
 * surfaces the same INVALID_ATTRIBUTE_DESCRIPTION error the NL attribute grammar
 * uses.
 */
export const sanitizeAttributeDescriptionSlash = (
  description: string,
  mode: SlashHandlingMode = "reject",
): { description: string; error?: CompilerError } => {
  if (!description.includes("/") || mode === "passthrough") {
    return { description };
  }
  if (mode === "encode") {
    return { description: description.replaceAll("/", "%2F") };
  }
  return {
    description,
    error: {
      type: COMPILER_ERROR_TYPES.INVALID_ATTRIBUTE_DESCRIPTION.id,
      message: "Invalid attribute, contains '/'",
    },
  };
};

export const sanitizeNamespace = <T extends string | undefined>(namespace: T): T => {
  if (!namespace) return namespace;
  let sanitizedNamespace = namespace.toLowerCase();
  if (sanitizedNamespace.endsWith(".docx")) {
    sanitizedNamespace = sanitizedNamespace.replace(".docx", "");
  } else if (sanitizedNamespace.endsWith(".xlsx")) {
    sanitizedNamespace = sanitizedNamespace.replace(".xlsx", "");
  }
  if (!sanitizedNamespace.startsWith("/")) {
    sanitizedNamespace = `/${sanitizedNamespace}`;
  }
  return sanitizedNamespace as T;
};

export const getNamespaceForDefinedIn = (
  definedIn: string | undefined,
  documents: ReleaseDocuments,
): string | undefined => {
  if (!definedIn) return undefined;

  if (!isDocumentId(definedIn)) {
    return sanitizeNamespace(definedIn);
  }

  const document = findReleaseDocument(documents, (document) => document.reference === definedIn);
  return sanitizeNamespace(document?.name);
};

export const getIdentifierValue = (node: any, graph: any) => {
  const query: any = {
    identifier: true,
    entity: node.entity,
  };
  if (node.index) query.index = node.index;
  if (node.parent_path) query.parent_path = node.parent_path;

  const identifier = find(graph._nodes, query);
  return getNodeResult(identifier as any);
};

export const getIdentifier = (path: any, graph: any) => {
  const parts = path.split("/");
  if (parts.length === 0 || parts.length === 1) return null;

  const index = parts[parts.length - 1];
  const entity = parts[parts.length - 2];
  const parentPath = parts.slice(0, parts.length - 2).join("/");
  let instanceIdentifier = null;
  let identifier = index;
  forEach(graph.nodes(), (id: any) => {
    const n = graph.node(id);
    if (n.identifier && !n.hidden && n.entity === entity && n.index === index) {
      if (!parentPath || n.parent_path === parentPath) {
        instanceIdentifier = n;
        return false; // Stop searching
      }
    }
  });
  if (instanceIdentifier) {
    const value = getNodeResult(instanceIdentifier);
    if (typeof value !== "undefined") identifier = value;
  }
  return identifier;
};

// Given a session graph (that has been expanded - eg: not minimal) returns
// an object of phases with the interactions that belong to that phase
// Note: interactions could appear in multiple phases
export const getInteractionsByPhase = (session: any) => {
  const seen: any = {}; // Stores nodes we've seen to prevent a rule loop
  const phases: any = {
    _nophase: {
      interactions: [],
    },
  };
  if (!session.graph.node) {
    //console.log('g', session.graph)
    session.graph = graphlib.json.read(session.graph);
  }
  const traverse = (node: GraphNode, currentPhase = "_nophase") => {
    if (node.hidden) return;
    if (node.phase && node.phaseName) {
      if (!phases[node.phaseName]) {
        phases[node.phaseName] = {
          attribute: node.path || node.id,
          interactions: [],
        };
      }
      currentPhase = node.phaseName;
    }
    if (node.triggers) {
      // It could trigger an interaction
      // We do this on the trigger meta rather than the triggered actual because we want to
      // show interactions that *could* occur
      if (!find(phases[currentPhase].interactions, { attribute: node.path || node.id })) {
        // Make sure we don't add the same interaction to the phases multiple times
        // Note: an attribute may have multiple triggers, so it's okay for the attribute path to
        // exists in the interactions already when we are processing it. That's why we check up here to
        // make sure we aren't adding if we process the attribute again
        node.triggers.forEach((t, i) => {
          const triggerInfo = node.triggered?.[i];
          //console.log('sessrel', session.relevantAttributes)
          let relevant = true;
          if (getNodeResult(node) === false)
            relevant = false; // As this trigger won't fire as it's conditions are not met
          else if (!triggerInfo && (session.relevantAttributes?.indexOf(node.path || node.id) ?? -1) === -1)
            relevant = false; // I haven't triggered yet, but I'm no longer relevant (meaning I am unlikely to fire)
          else if (typeof getNodeResult(node) === "undefined" || getNodeResult(node) === null) {
            if (t.type === "task") {
              // For a task I'm relevant if my outcome is still required
              if ((session.relevantAttributes?.indexOf(node.path || node.id) ?? -1) === -1) relevant = false;
            } // Otherwise I'm relevant
          }
          let nodeContext = {};
          if (node.entity !== "global") {
            nodeContext = {
              entity: node.entity,
              index: node.index,
              parent: node.parent_path,
            };
          }
          const title = processText(t.title, nodeContext, {
            graph: session.graph,
          });
          phases[currentPhase].interactions.push({
            type: t.type,
            title: title ? title.text : t.title,
            // @ts-ignore
            outcome: getNodeResult(session.graph.node(t.outcome)),
            created: triggerInfo?.when,
            // @ts-ignore
            outcomeAttribute: t.outcome,
            interaction: triggerInfo?.interaction,
            assignedTo: "", // TODO: this is tricky as it's in the tasks API for now - wait until we've built the assignment work and fix from there
            attribute: node.path || node.id,
            relevant: relevant,
          });
        });
      }
    }
    if (seen[node.path || node.id]) return; // We've already checked this, don't process so we can stop a rule loop
    // We do this down here so that if a node has multiple parents, and those parents potentially have different phases than the node
    // will still be assigned to the multiple phases
    seen[node.path || node.id] = true;

    const children = session.graph.successors(node.path || node.id);
    if (children && children.length > 0) {
      for (const c of children) {
        traverse(session.graph.node(c), currentPhase);
      }
    }
  };
  traverse(session.graph.node(session.goal));
  return phases;
};

export const isDerivedNode = (node: GraphNode) => {
  return (
    typeof node.value !== "undefined" ||
    typeof node.conditions !== "undefined" ||
    typeof node.rows !== "undefined" ||
    typeof node.otherwise !== "undefined" ||
    typeof node.inferred !== "undefined"
  );
};

export const isInputNode = (node: GraphNode) => {
  return !isDerivedNode(node);
};

export const NODE_KEY = Symbol("NodeKey");

export const getNodeKey = (node: GraphNode, documents: ReleaseDocuments): NodeKey => {
  if (node[NODE_KEY]) {
    if (node.path !== undefined && node[NODE_KEY]?.path !== node.path) {
      // cannot use console...
      // console.error("NODE_KEY was somehow transferred to a different node by accident", node.path, node[NODE_KEY]);
      node[NODE_KEY] = undefined;
    } else {
      return node[NODE_KEY];
    }
  }

  const namespace = getNamespaceForDefinedIn(node.definedIn, documents);

  // TODO: entity keys
  //const entityPath = node.path?.split("/").slice(0, -1).join("/") || undefined;

  const key: NodeKey = {
    description: normalizeText(node.description as any),
    namespace: namespace,
    guid: node.id,
    entityKey: undefined,
    entityPath: node.path?.split("/").slice(0, -1).join("/") || undefined,
    path: node.path ?? node.id,
    namespacedDescription: namespace
      ? `${namespace}/${normalizeText(node.description as string)}`
      : normalizeText(node.description as string),
  };
  Object.defineProperty(node, NODE_KEY, {
    value: key,
    // prevent spread copying properites to new nodes
    enumerable: false,
  });
  return key;
};

export const getPathFromContext = (context: any) => {
  if (!context) return undefined;
  else if (context.entity === "global") return undefined;
  let parent = context.parent || context.parent_path;
  if (parent && typeof parent !== "string") parent = getPathFromContext(parent);
  const index = context.index || context.id;
  return parent ? `${parent}/${context.entity}/${index}` : `${context.entity}/${index}`;
};
